/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { Images, Prayer } from "@packages/sdk";
import type { MouseEvent } from "react";

import { spacing } from "../../../../../../global/emotion";
import { useFavoritePrayerAction } from "../../../../../hooks/useFavoriteAction";
import { AddToQueue } from "../../../../blocks/other/AddToQueue/AddToQueue.emotion";
import { IconButton } from "../../../_core/IconButton/IconButton.emotion";
import { StarIcon } from "../../../icons/Icon/GeneralIcons";
import { MoreIcon } from "../../../icons/Icon/NavigationIcons";
import type { DropdownMenuProps } from "../../../modals/DropdownMenu/DropdownMenu.emotion";
import { DropdownMenu } from "../../../modals/DropdownMenu/DropdownMenu.emotion";
import { DropdownMenuItem } from "../../../modals/DropdownMenuItem/DropdownMenuItem.emotion";
import ContentMoreMenuHeader from "./ContentMoreMenuHeader.emotion";

const dropdownMenuContentStyle: Interpolation = { paddingTop: spacing.l };

type PrayerMoreMenuProps = DropdownMenuProps & {
  imageSrc?: Images;
  prayer: Pick<
    Prayer,
    | "id"
    | "is_favorite"
    | "title"
    | "label_desc"
    | "lengths"
    | "has_access"
    | "has_video"
  >;
};

export const PrayerMoreMenu = ({
  prayer,
  imageSrc,
  ...props
}: PrayerMoreMenuProps) => {
  const updateFavorite = useFavoritePrayerAction({
    id: prayer.id,
    has_access: prayer.has_access,
  });
  const t = useTranslations();

  const onClickFavorite = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    updateFavorite();
  };

  return (
    <DropdownMenu
      align="end"
      contentCSS={dropdownMenuContentStyle}
      modal={false}
      trigger={
        <IconButton
          icon={<MoreIcon />}
          onClick={(e) => {
            e.preventDefault();
          }}
          variant="transparentPrimary"
        />
      }
      {...props}
    >
      <ContentMoreMenuHeader
        imageSrc={imageSrc}
        metadata={prayer.lengths}
        subtitle={prayer.label_desc}
        title={prayer.title}
        hasVideoContent={prayer.has_video}
      />
      <DropdownMenuItem
        onClick={onClickFavorite}
        startIcon={prayer.is_favorite ? <StarIcon.Off /> : <StarIcon.Outline />}
      >
        {prayer.is_favorite
          ? t("dialog_prayer_options_unfavorite")
          : t("dialog_prayer_options_favorite")}
      </DropdownMenuItem>
      <AddToQueue id={prayer.id} type={"prayer"} />
    </DropdownMenu>
  );
};
